"use client";

import Section from "@/components/section";
import { motion } from "src/lib/motion";
import { Check } from "lucide-react";
import { Icons } from "@/components/icons";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import financialVideo from "@/assets/videos/Financial-Services-Web-1.mp4";
import { useTranslations } from "@/lib/hooks/use-translations";
import { useParams } from "next/navigation";

export default function CaseStudy() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params?.locale as string || 'en';

  // Get case study details from translations
  const details = t('pages.solutions.ai-consulting.case-study.details') as string[];

  // Determine contact URL based on locale
  const contactUrl = locale === 'de' ? '/de/kontakt' : locale === 'zh-CN' ? '/zh-CN/contact' : '/contact';

  return (
    <Section
      id="case-study"
      title={t('pages.solutions.ai-consulting.case-study.title')}
      subtitle={t('pages.solutions.ai-consulting.case-study.subtitle')}
      className="bg-background"
      titleBlockClassName="lg:pb-16"
    >
      <div className="">
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative h-full flex items-center"
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              className="rounded-lg object-contain bg-white dark:bg-black w-full h-full"
              aria-label="AI-Powered Financial Services Dashboard - Showcasing real-time analytics and intelligent automation"
            >
              <source src={financialVideo.replace('.mp4', '.webm')} type="video/webm" />
              <source src={financialVideo} type="video/mp4" />
            </video>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-lg text-muted-foreground max-md:text-center">
              {t('pages.solutions.ai-consulting.case-study.description')}
            </p>

            <div className="space-y-4 mt-6">
              {details.map((detail, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="mt-1 bg-primary/10 rounded-full p-1">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                  <p>{detail}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col justify-center sm:flex-row gap-4 !mt-10">
              <Link
                href={contactUrl}
                className={cn(
                  buttonVariants({ variant: "default" }),
                  "w-full sm:w-auto text-background flex gap-2"
                )}
              >
                <Icons.logoIcon className="h-6 w-6" />
                {t('pages.solutions.ai-consulting.case-study.buttons.getStarted')}
              </Link>
              <button
                onClick={() => document.getElementById("faq")?.scrollIntoView({ behavior: "smooth" })}
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "w-full sm:w-auto"
                )}
              >
                {t('pages.solutions.ai-consulting.case-study.buttons.readMore')}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}
